"use client";

import React, { useState } from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import ServiceCategory from "@/components/shared/ServiceCategory";
import RelatedProject from "@/components/shared/RelatedProject";
import { getImageUrl } from "@/utils/utils";
import RichContent from "@/components/ui/RichContent";

interface ServiceDetailClientProps {
  serviceDetailPageData: any;
  serviceDetail: any;
  lang: string;
}

const ServiceDetailClient: React.FC<ServiceDetailClientProps> = ({
  serviceDetailPageData,
  serviceDetail,
  lang,
}) => {
  const [currentProjectsPage, setCurrentProjectsPage] = useState(0);

  // Animation variants
  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: 60,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "circOut" as const,
      },
    },
  };

  // Pagination logic for related projects
  const projectsPerPage = 3;
  const totalProjectsPages = Math.ceil(
    (serviceDetail?.relatedProjects?.length || 0) / projectsPerPage
  );
  const displayedProjects =
    serviceDetail?.relatedProjects?.slice(
      currentProjectsPage * projectsPerPage,
      (currentProjectsPage + 1) * projectsPerPage
    ) || [];

  const nextProjectsPage = () => {
    setCurrentProjectsPage((prev) => (prev + 1) % totalProjectsPages);
  };

  const previousProjectsPage = () => {
    setCurrentProjectsPage(
      (prev) => (prev - 1 + totalProjectsPages) % totalProjectsPages
    );
  };

  return (
    <motion.div
      className="bg-[#f4f5f6] py-12 sm:py-16 lg:py-[100px]"
      initial="hidden"
      animate="visible"
      variants={sectionVariants}
    >
      <div className="mx-auto">
        {/* Header */}
        <div className="flex flex-col items-center gap-4 lg:gap-[17px] mb-8 sm:mb-12 lg:mb-[50px]">
          <motion.h2
            className="text-[#0a1119] text-center text-2xl sm:text-3xl lg:text-[40px] font-medium leading-tight lg:leading-[58px] lg:tracking-[-1.44px]"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            {serviceDetail?.title || ""}
          </motion.h2>
          <div className="w-[50px] h-[2px] bg-[#1a5852]"></div>
        </div>

        {/* Service Details */}
        <div className="bg-white relative overflow-hidden rounded-lg lg:rounded-none shadow-[0_30px_60px_0_rgba(0,0,0,0.08)] group/progress">
          {/* Background Pattern - Hidden on mobile for better performance */}
          <div className="hidden lg:block absolute right-[-1679px] top-[-117.556px] opacity-30 mix-blend-luminosity">
            <svg
              width="2996"
              height="1331.556"
              viewBox="0 0 1318 1202"
              fill="none"
            >
              <g opacity="0.3" clipPath="url(#clip0_3002_205)">
                <path
                  d="M2996.5 -117.555H0.5V1214H2996.5V-117.555Z"
                  fill="#F0F0F0"
                />
                <defs>
                  <clipPath id="clip0_3002_205">
                    <rect
                      width="2996"
                      height="1331.56"
                      fill="white"
                      transform="translate(0.5 -117.556)"
                    />
                  </clipPath>
                </defs>
              </g>
            </svg>
          </div>

          <div className="relative p-4 sm:p-8 lg:p-[50px]">
            <div className="mx-auto">
              {/* Description */}
              <motion.div
                className="text-[#3d434c] text-base sm:text-xl lg:text-[20px] font-normal leading-relaxed lg:leading-8 text-justify mb-8 sm:mb-12 lg:mb-11"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <RichContent html={serviceDetail?.description} />
              </motion.div>

              {/* Image Cards Grid */}
              {serviceDetail?.imageCards &&
                serviceDetail?.imageCards.length > 0 && (
                  <div className="space-y-6 sm:space-y-8 lg:space-y-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
                      {serviceDetail?.imageCards.map((card, index) => (
                        <ServiceCategory
                          key={card.id}
                          id={card.id}
                          title={card.label}
                          image={getImageUrl(card.image?.url)}
                          index={index}
                        />
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </div>

          {/* Progress Bar */}
          <div className="flex h-[2px] bg-gray-25 relative">
            <div
              className={`h-[2px] bg-[#1c8579] absolute left-0 top-0 transition-all duration-500 w-0 group-hover/progress:w-full`}
            ></div>
          </div>
        </div>

        {/* Related Projects Section */}
        {serviceDetail?.relatedProjects &&
          serviceDetail?.relatedProjects.length > 0 && (
            <div className="bg-white relative overflow-hidden rounded-lg lg:rounded-none shadow-[0_30px_60px_0_rgba(0,0,0,0.08)] mt-4 group/progress">
              {/* Background Pattern */}
              <div className="hidden lg:block absolute right-[-699px] top-[-481.5px] opacity-10 mix-blend-luminosity">
                <Image
                  src="/images/services/services-3-680997bdc89cc83aa2a6c6ec5a4d464a8ba12404.jpg"
                  alt="Background pattern"
                  width={2015}
                  height={1345}
                  className="object-cover"
                />
              </div>

              <div className="relative p-4 sm:p-8 lg:p-[50px]">
                <div className="mx-auto">
                  {/* Section Header */}
                  <div className="flex items-center gap-[10px] pb-2 border-b border-[#f4f5f6] mb-6">
                    <h3 className="text-[#1a1d21] text-xl sm:text-2xl lg:text-[28px] font-medium leading-tight lg:leading-[34px] lg:tracking-[-0.72px]">
                      {serviceDetailPageData?.relatedProjectsText ||
                        "Related Projects"}
                    </h3>
                  </div>
                  <Image
                    src="/images/shared/details-bottom-bg.png"
                    alt="Background pattern"
                    fill
                    className="object-cover object-bottom luminosity mix-blend-luminosity pointer-events-none select-none"
                  />

                  {/* Projects Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-6">
                    {displayedProjects?.map((project, index) => (
                      <RelatedProject
                        key={project.id}
                        id={project.id}
                        title={project.title}
                        image={getImageUrl(project?.image?.url || "")}
                        slug={project.slug}
                        index={index}
                        lang={lang}
                      />
                    ))}
                  </div>

                  {/* Navigation Arrows */}
                  {totalProjectsPages > 1 && (
                    <div className="flex items-center gap-6 justify-center">
                      <button
                        onClick={previousProjectsPage}
                        className="flex items-center justify-center w-[46px] h-[46px] p-[13px] border border-[#88909f] rounded-[25px] hover:bg-[#f4f5f6] transition-colors cursor-pointer"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 21 20"
                          fill="none"
                        >
                          <path
                            d="M8.4763 4.94165L3.41797 9.99998L8.4763 15.0583"
                            stroke="#88909F"
                            strokeWidth="1.25"
                            strokeMiterlimit="10"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M17.5836 10H3.55859"
                            stroke="#88909F"
                            strokeWidth="1.25"
                            strokeMiterlimit="10"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={nextProjectsPage}
                        className="flex items-center justify-center w-[46px] h-[46px] p-[13px] border border-[#88909f] rounded-[25px] hover:bg-[#f4f5f6] transition-colors cursor-pointer"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 21 20"
                          fill="none"
                        >
                          <path
                            d="M12.5234 4.94165L17.5818 9.99998L12.5234 15.0583"
                            stroke="#88909F"
                            strokeWidth="1.25"
                            strokeMiterlimit="10"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M3.41797 10H17.443"
                            stroke="#88909F"
                            strokeWidth="1.25"
                            strokeMiterlimit="10"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Progress Bar */}
              <div className="flex h-[2px] bg-gray-25 relative">
                <div
                  className={`h-[2px] bg-[#1c8579] absolute left-0 top-0 transition-all duration-500 w-0 group-hover/progress:w-full`}
                ></div>
              </div>
            </div>
          )}
      </div>
    </motion.div>
  );
};

export default ServiceDetailClient;
